import { MoneyPh } from '~shared/ui/money-ph';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import type { OrderTableData } from '../model/orders-table-data';
import { OrderStatusBadge } from '../ui/order-status-badge';

const columnHelper = createColumnHelper<OrderTableData>();

export enum ColumnId {
  SHOP_ORDER_ID = 'shopOrderId',
  ORDER_NUMBER = 'orderNumber',
  ORDER_DATE = 'orderDate',
  CUSTOMER_NAME = 'customerName',
  BUSINESS_NAME = 'businessName',
  STORE_NAME = 'storeName',
  ORDER_STATUS = 'orderStatus',
  ITEMS_NUMBER = 'itemsNumber',
  TOTAL_PRICE = 'totalPrice',
}

type ColumnOption = { id: ColumnId; label: string };

export const COLUMN_OPTIONS: ColumnOption[] = [
  { id: ColumnId.ORDER_DATE, label: 'Date' },
  { id: ColumnId.SHOP_ORDER_ID, label: 'Order number' },
  { id: ColumnId.ORDER_NUMBER, label: 'Internal order number' },
  { id: ColumnId.CUSTOMER_NAME, label: 'Customer name' },
  { id: ColumnId.BUSINESS_NAME, label: 'Business' },
  { id: ColumnId.STORE_NAME, label: 'Store' },
  { id: ColumnId.ORDER_STATUS, label: 'Status' },
  { id: ColumnId.ITEMS_NUMBER, label: 'Number of items' },
  { id: ColumnId.TOTAL_PRICE, label: 'Total' },
];

export const ALL_COLUMN_IDS = COLUMN_OPTIONS.map((option) => option.id);

export const SORT_OPTIONS: ColumnOption[] = [
  { id: ColumnId.ORDER_DATE, label: 'Date' },
];

export const columns = [
  columnHelper.accessor((row) => row.orderTimestampUTC, {
    id: ColumnId.ORDER_DATE,
    meta: { width: 120, minWidth: 120, sortable: true },
    cell: (data) => {
      const date = new Date(data.getValue());
      return (
        <div className="whitespace-nowrap">
          <p className="my-0 leading-[24px]">{format(date, 'dd-MM-yyyy')}</p>
          <p
            className={`
              my-0 typography-text-xs leading-[16px] text-[rgba(0,0,0,0.54)]
            `}
          >
            {format(date, 'h:mm a')}
          </p>
        </div>
      );
    },
    header: () => <span>Date</span>,
  }),
  columnHelper.accessor((row) => row.shopOrderId, {
    id: ColumnId.SHOP_ORDER_ID,
    meta: { minWidth: 130, width: '15%' },
    cell: (data) => {
      const id = data.getValue();
      return id ? <div className="line-clamp-1 max-w-full">{id}</div> : null;
    },
    header: () => <span>Order number</span>,
  }),
  columnHelper.accessor((row) => row.orderNumber, {
    id: ColumnId.ORDER_NUMBER,
    meta: { minWidth: 120, width: '15%' },
    cell: (data) => <span>{data.getValue()}</span>,
    header: () => <span>Internal order&nbsp;number</span>,
  }),
  columnHelper.accessor((row) => row.customerFullName, {
    id: ColumnId.CUSTOMER_NAME,
    meta: { minWidth: 130, width: '20%' },
    cell: (data) => <span>{data.getValue()}</span>,
    header: () => <span>Customer name</span>,
  }),
  columnHelper.accessor((row) => row.merchantName, {
    id: ColumnId.BUSINESS_NAME,
    meta: { minWidth: 160, width: '20%' },
    cell: (data) => (
      <div className="max-w-[250px] overflow-hidden text-ellipsis">
        {data.getValue()}
      </div>
    ),
    header: () => <span>Business</span>,
  }),
  columnHelper.accessor((row) => row.posName, {
    id: ColumnId.STORE_NAME,
    meta: { minWidth: 160, width: '20%' },
    cell: (data) => (
      <div className="max-w-[250px] overflow-hidden text-ellipsis">
        {data.getValue()}
      </div>
    ),
    header: () => <span>Store</span>,
  }),
  columnHelper.accessor((row) => row.orderStatus, {
    id: ColumnId.ORDER_STATUS,
    meta: { minWidth: 100, width: '10%' },
    cell: (data) => <OrderStatusBadge status={data.getValue()} />,
    header: () => <span>Status</span>,
  }),
  columnHelper.accessor((row) => row.itemsCount, {
    id: ColumnId.ITEMS_NUMBER,
    meta: { width: 90 },
    cell: (data) => <span>{data.getValue()}</span>,
    header: () => <span>Number of&nbsp;items</span>,
  }),
  columnHelper.accessor((row) => row.totalAmount, {
    id: ColumnId.TOTAL_PRICE,
    meta: { minWidth: 100, width: '15%', align: 'right' },
    cell: (data) => (
      <div className="flex justify-end">
        <MoneyPh value={data.getValue()} />
      </div>
    ),
    header: () => <p className="flex-1 text-right">Total</p>,
  }),
];
